import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import usePageMeta from '../hooks/usePageMeta';

// A contractor's SMS privacy policy and terms, at /sms-policy/:slug/privacy
// and /sms-policy/:slug/terms. These are the URLs the contractor's A2P 10DLC
// campaign registers with the carriers (server/services/smsCampaignContent.js
// policyUrls), so carrier reviewers and the contractor's customers both read
// them. No login. The text comes from the server
// (services/smsPolicyRoutes.js), built from the company name and phone; this
// page only lays it out.
//
// noindex here and an X-Robots-Tag from netlify.toml: a per-company page must
// not compete with the landing page in search. The path stays crawlable so the
// noindex can be read, the same as the intake form.

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const KINDS = ['privacy', 'terms'];

const styles = `
.sms-policy-page { min-height: 100vh; background: #f4f6f8; padding: 24px 16px; box-sizing: border-box; }
.sms-policy-card { max-width: 720px; margin: 0 auto; background: #fff; border-radius: 8px; padding: 28px 24px; color: #1f2933; line-height: 1.55; }
.sms-policy-card h1 { font-size: 1.5rem; margin: 0 0 16px; }
.sms-policy-card h2 { font-size: 1.05rem; margin: 20px 0 6px; }
.sms-policy-card p { margin: 0; }
.sms-policy-links { margin-top: 28px; padding-top: 16px; border-top: 1px solid #e4e7eb; font-size: 0.9rem; }
.sms-policy-links a { margin-right: 16px; }
`;

function SmsPolicy() {
  const { slug, kind } = useParams();
  const known = KINDS.includes(kind);
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState('');

  usePageMeta({
    title: doc ? doc.title : kind === 'terms' ? 'SMS Terms and Conditions' : 'SMS Privacy Policy',
    robots: 'noindex',
  });

  useEffect(() => {
    if (!known) return undefined;
    let cancelled = false;
    setDoc(null);
    setError('');
    axios.get(`${API_URL}/api/public/sms-policy/${encodeURIComponent(slug)}/${kind}`)
      .then((res) => { if (!cancelled) setDoc(res.data); })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response && err.response.status === 404 ? 'This page was not found.' : 'This page could not be loaded. Please try again.');
      });
    return () => { cancelled = true; };
  }, [slug, kind, known]);

  const shell = (children) => (
    <div className="sms-policy-page"><style>{styles}</style><div className="sms-policy-card">{children}</div></div>
  );

  if (!known) return shell(<p>This page was not found.</p>);
  if (error) return shell(<p role="alert">{error}</p>);
  if (!doc) return shell(<p>Loading...</p>);

  const other = kind === 'privacy' ? 'terms' : 'privacy';
  return shell(
    <>
      <h1>{doc.title}</h1>
      {doc.sections.map((s) => (
        <section key={s.heading}>
          <h2>{s.heading}</h2>
          <p>
            {s.strong && s.body.startsWith(s.strong)
              ? <><strong>{s.strong}</strong> {s.body.slice(s.strong.length).trim()}</>
              : s.body}
          </p>
        </section>
      ))}
      <div className="sms-policy-links">
        <Link to={`/sms-policy/${encodeURIComponent(slug)}/${other}`}>
          {doc.companyName} SMS {other === 'terms' ? 'Terms and Conditions' : 'Privacy Policy'}
        </Link>
      </div>
    </>
  );
}

export default SmsPolicy;
